<template>
  <GamMainView
    :class="[
      'locations-view',
      { prevent: imageExpanded, 'list-open': isListOpen, 'add-open': isAddingLocation && !isMobile },
    ]"
    :style="{
      '--drawer-bottom-offset': `${mapStore.bottomDrawerHeight}px`,
      '--controls-bottom-gap': controlsBottomGap,
    }"
  >
    <LocationList
      v-if="isUserLocated && !isCreateFlow && !mapStore.seedPickerMode"
      :key="`user-${user?.id ?? 'none'}`"
    />

    <SpotOverlay />

    <MapModes v-if="showMapModes" :class="['mobile-map-modes', { 'mobile-map-modes-only': isCreateFlow }]" />

    <template v-if="isAuthenticated">
      <AddLocation v-if="route.query.side_panel === 'add_location'" />

      <EditLocation v-if="route.query.side_panel === 'edit_location'" />
    </template>

    <div v-if="isUserLocated" class="map-wrapper" :class="{ 'is-absolute': isMobile }">
      <GamMap v-bind="getMapConfig" @update="updateSelectedLocation" @plant="handlePlantBoard" />
    </div>

    <Transition name="fade">
      <div v-if="checkInMode" class="proposal-mode-header">
        <span class="checkin-message">
          {{ localize('checkin.map.message') }}
          <span class="checkin-message-sub">{{ localize('checkin.map.message.sub') }}</span>
        </span>
        <GamButton
          class="proposal-mode-close"
          :left-icon="GamIconName.X_CLOSE"
          :size="GamButtonSize.SMALL"
          :variant="GamButtonVariant.STEKLO"
          is-icon-only
          @click="handleCheckInClose()"
        />
      </div>
    </Transition>

    <Transition name="preview-slide">
      <div
        v-if="isAuthenticated && mapProposalLocation && checkInMode"
        ref="checkinCardRef"
        class="map-proposal-overlay checkin-overlay"
      >
        <MapCheckInCard :location="mapProposalLocation" @checked-in="handleCheckInComplete" />
      </div>
      <div v-else-if="mapProposalLocation && showProposalCard" ref="proposalOverlayRef" class="map-proposal-overlay">
        <MapProposalCard
          ref="proposalCardRef"
          :location="mapProposalLocation"
          :is-challenge="!!challengeUserId"
          :challenge-user-id="challengeUserId"
          :default-title="proposalDefaultTitle"
          @close="handleMapProposalClose"
          @created="handleMapProposalCreated($event)"
        />
      </div>
    </Transition>

    <template v-if="!mapStore.seedPickerMode">
      <ScreenNav v-if="showMapNav" active="map" @profile="goToProfile" @sparks="goToSparks" />

      <!-- Proposal search drawer -->
      <Transition name="drawer-slide">
        <div v-if="showProposalDrawer" class="proposal-drawer-overlay">
          <div ref="proposalDrawerRef" class="proposal-drawer-card">
            <div class="proposal-drawer-title">{{ proposalDrawerTitle }}</div>
            <template v-if="mapProposalLocation">
              <div class="proposal-drawer-body proposal-candidate">
                <template v-if="isProposalCandidateVirtual">
                  <div class="proposal-candidate-info">
                    <GamIcon :name="GamIconName.LOCATION_EMPTY" :size="GamIconSize.NORMAL" is-current />
                    <span class="proposal-candidate-address">{{ proposalAddressLabel }}</span>
                  </div>
                  <GamInput
                    v-model="mapProposalLocation.name"
                    :name="GamInputName.GAM_NAME"
                    :placeholder="'locations.add.form.name.placeholder'"
                    :left-icon="GamIconName.EDIT"
                  />
                </template>
                <div v-else class="proposal-candidate-info">
                  <GamIcon :name="GamIconName.LOCATION_EMPTY" :size="GamIconSize.NORMAL" is-current />
                  <span class="proposal-candidate-name">{{ mapProposalLocation.name }}</span>
                </div>
              </div>
              <div class="proposal-drawer-ctas">
                <div class="proposal-cta-circle" @click="handleClearProposalCandidate">
                  <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
                </div>
                <div class="proposal-cta-circle proposal-cta-arrow" @click="handleConfirmProposalCandidate">
                  <GamIcon :name="GamIconName.ARROW_RIGHT" :size="GamIconSize.LARGE" is-current />
                </div>
              </div>
            </template>
            <template v-else>
              <div class="proposal-drawer-body">
                <template v-if="mapStore.walkFromReveal">
                  <span class="proposal-hint-label">{{ localize('walk.proximity.select_nearby') }}</span>
                </template>
                <GamSimpleLocationSearch v-else v-bind="getProposalSearchConfig" />
              </div>
              <div class="proposal-drawer-ctas">
                <div class="proposal-cta-circle" @click="handleExitProposalMode()">
                  <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
                </div>
              </div>
            </template>
          </div>
        </div>
      </Transition>

      <!-- Walk action CTAs removed — walk CTA is the bottom-center walk-cta-group -->
    </template>

    <div v-if="showMapNav && !mapStore.seedPickerMode" class="map-install-wrapper">
      <div v-if="showIOSInstructions" class="ios-install-backdrop" @click="dismissIOSInstructions" />
      <Transition name="gps-popup">
        <div v-if="showIOSInstructions" class="ios-install-popup" @click="dismissIOSInstructions">
          <div class="ios-install-popup-content">
            <span class="ios-install-title">{{ localize('pwa.install.ios.title') }}</span>
            <span>
              {{ localize(isMacSafari ? 'pwa.install.mac.instruction.prefix' : 'pwa.install.ios.instruction.prefix') }}
              <svg class="ios-share-icon" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
                <path
                  d="M25 10l-7 7m7-7l7 7m-7-7v22M15 22v14a2 2 0 002 2h16a2 2 0 002-2V22"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ localize(isMacSafari ? 'pwa.install.mac.instruction.suffix' : 'pwa.install.ios.instruction.suffix') }}
              <svg
                v-if="isMacSafari"
                class="ios-add-icon"
                viewBox="0 0 50 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
                <rect x="11" y="14" width="28" height="22" rx="4" stroke="currentColor" stroke-width="2" />
                <path d="M16 30h18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
              </svg>
              <svg v-else class="ios-add-icon" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="50" height="50" rx="7" fill="currentColor" fill-opacity="0.08" />
                <rect x="12" y="12" width="26" height="26" rx="5" stroke="currentColor" stroke-width="2" />
                <path d="M25 19v12M19 25h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </span>
          </div>
        </div>
      </Transition>
      <div v-if="canInstall" class="map-nav-install" @click="installApp">
        <img src="/favicon/app-icon-v6.png" alt="" class="install-icon-img" />
      </div>
    </div>

    <div v-if="(showMapNav || proposalMode || isCreateFlow) && !mapStore.seedPickerMode" class="map-controls-right">
      <Transition name="fade">
        <GamButton
          v-if="mapHeading !== 0"
          :class="['compass-btn', { rotated: mapHeading !== 0 }]"
          :style="{ '--compass-rotation': `-${mapHeading}deg` }"
          v-bind="getCompassButton"
        />
      </Transition>
      <GamButton v-bind="getSatelliteButton" />
      <div
        :class="gpsButtonClasses"
        @touchend.prevent="handlePreciseLocationRequest"
        @click="handlePreciseLocationRequest"
      >
        <GamIcon :name="GamIconName.TARGET" size="18px" is-current />
      </div>
      <div
        v-if="showMapNav && isAuthenticated"
        class="map-nav-proposal"
        @touchend.prevent="startMapProposal"
        @click="startMapProposal"
      >
        <GamIcon :name="GamIconName.PLUS" is-current />
      </div>
    </div>

    <!-- Sphere button hidden - activity circles moved to profile -->
    <!-- <div
      v-if="showMapNav && isAuthenticated && !mapStore.seedPickerMode && !isInWalkSetup"
      class="map-nav-seed-wrapper"
      @touchend.prevent="handleSeedClick"
      @click="handleSeedClick"
    >
      <GamSphere />
    </div> -->

    <BoardsOverlay :visible="showBoardsOverlay" @close="showBoardsOverlay = false" />

    <div v-if="mapStore.seedPickerMode" class="seed-picker-top">
      <span class="seed-picker-instruction">{{ localize('seed.picker.instruction') }}</span>
    </div>

    <div v-if="mapStore.seedPickerMode" class="seed-picker-close" @click="mapStore.exitSeedPickerMode()">
      <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
    </div>

    <MapWizard v-if="showMapWizard && isAuthenticated && !showBoardsOverlay" @done="showMapWizard = false" />

    <!-- Booklet is mounted globally in App.vue -->
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { LocationGambitDto } from '@/core/data/gambit/gambit.interface';
import type { BaseLocationDto, MapLocationDto } from '@/core/data/location/location.interface';
import { LocationType, MapLocationType } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { isMacSafari, isMobile } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useMapStore } from '@/stores/map.store';
import { useMarkerStore } from '@/stores/marker.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useSeedStore } from '@/stores/seed.store';
import { useTableStore } from '@/stores/table.store';
import { useWalkStore } from '@/stores/walk.store';
import GamMap from '@/views/components/gam-map/GamMap.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamInput from '@/views/components/GamInput.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import GamSimpleLocationSearch from '@/views/components/GamSimpleLocationSearch.vue';
import ScreenNav from '@/views/components/ScreenNav.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { useDrawerHeight } from '@/views/composables/hooks/useDrawerHeight';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import { useInstallPrompt } from '@/views/composables/hooks/useInstallPrompt';
import { notifyLocationDenied, notifyLocationUnavailable } from '@/views/composables/hooks/useLocationHelp';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import type { GamMapType } from '@/views/composables/models/components/GamMap';
import MapCheckInCard from '@/views/main/locations/cards/MapCheckInCard.vue';
import MapProposalCard from '@/views/main/locations/cards/MapProposalCard.vue';
import MapWizard from '@/views/main/locations/components/MapWizard.vue';
import BoardsOverlay from '@/views/main/locations/modals/BoardsOverlay.vue';
import AddLocation from '@/views/main/locations/side-panels/AddLocation.vue';
import { handleCreateLocation } from '@/views/main/locations/side-panels/locationActions';
import LocationList from '@/views/main/locations/side-panels/LocationsList.vue';
import { useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import EditLocation from './side-panels/EditLocation.vue';
import SpotOverlay from './SpotOverlay.vue';
import MapModes from './widgets/MapModes.vue';

const listId = GamListId.LOCATIONS;

const appStore = useAppStore();
const { imageExpanded } = storeToRefs(appStore);

const mapStore = useMapStore();
const {
  selectedLocationId,
  currentZoom,
  mapProposalLocation,
  proposalMode,
  checkInMode,
  challengeUserId,
  satelliteMode,
  mapHeading,
} = storeToRefs(mapStore);

const proposalAddressLabel = computed(() => {
  const loc = mapProposalLocation.value;
  if (!loc) return '';
  return loc.address || `${loc.coordinates.lat.toFixed(5)}, ${loc.coordinates.lng.toFixed(5)}`;
});

const proposalDrawerTitle = computed(() => {
  switch (mapStore.walkSpotRole) {
    case 'start':
      return localize('locations.map.proposal.title.start_spot');
    case 'reveal':
      return localize('locations.map.proposal.title.reveal_spot');
    default:
      return mapProposalLocation.value
        ? localize('locations.map.proposal.title.irl_spot')
        : localize('locations.map.proposal.title.where_to_meet');
  }
});

const markerStore = useMarkerStore(GamListId.MARKERS)();

const geolocationStore = useGeolocationStore();
const { isUserLocated, userLocation, isLocationPermissionGranted, isLocationPermissionDenied } =
  storeToRefs(geolocationStore);

const tableStore = useTableStore<BaseLocationDto>(listId)();
const { selectedItem } = storeToRefs(tableStore);

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);

const navigationStore = useNavigationStore();
const { openResultListSignal, addSpotSignal } = storeToRefs(navigationStore);

const router = useRouter();
const route = useRoute();

const proposalCardRef = ref<InstanceType<typeof MapProposalCard> | null>(null);
const proposalDrawerRef = ref<HTMLElement | null>(null);
const proposalOverlayRef = ref<HTMLElement | null>(null);
const checkinCardRef = ref<HTMLElement | null>(null);
useDrawerHeight(proposalDrawerRef);
useDrawerHeight(checkinCardRef);
const proposalConfirmed = ref(false);
const showBoardsOverlay = ref(false);

const showMapWizard = ref(false);
const seedStore = useSeedStore();
const { fire: fireConfetti } = useConfetti();

const walkStore = useWalkStore();

const {
  canInstall,
  showIOSInstructions,
  init: initInstallPrompt,
  install: installApp,
  dismissIOSInstructions,
} = useInstallPrompt();

const closeBoardsModal = () => {
  showBoardsOverlay.value = false;
};

const isPlanting = ref(false);

const enterSpotPicker = async () => {
  closeBoardsModal();

  if (!isLocationPermissionGranted.value) {
    const coords = await geolocationStore.requestFreshPosition();
    if (!coords) {
      useToast().error(localize('seed.picker.permission_denied'), { id: 'seed-permission-denied' });
      return;
    }
  }

  mapStore.enterSeedPickerMode();

  await nextTick();
  // @ts-ignore
  window.gMap?.map?.setZoom(17);
  if (userLocation.value?.location) {
    // @ts-ignore
    window.gMap?.map?.panTo(userLocation.value.location);
  }
};

const handlePlantBoard = async () => {
  const spot = mapStore.selectedSeedSpot;
  if (!spot || isPlanting.value) return;

  isPlanting.value = true;
  try {
    let locationId = spot.id;

    if (locationId === mapStore.VIRTUAL_LOCATION_ID) {
      const result = await handleCreateLocation({
        name: spot.name || spot.address || 'New spot',
        address: spot.address || '',
        type: LocationType.CHESS_FRIENDLY,
        coordinates: spot.coordinates,
        chessFeatures: [],
        generalFeatures: [],
        photos: [],
        private: false,
      });
      if (!result || typeof result !== 'object' || !('id' in result)) return;
      locationId = (result as MapLocationDto).id;
    }

    const result = await gambits.chessboardService.create({ locationId });
    if (result.data) {
      mapStore.exitSeedPickerMode();
      seedStore.fetchStats();
      await router.push({ path: `/board/${result.data.id}` });
      fireConfetti({ icon: GamIconName.GAMBIT, label: localize('seed.confetti.label') });
    }
  } finally {
    isPlanting.value = false;
  }
};

const proposalDefaultTitle = computed(() => {
  return '';
});

const updateSelectedLocation = (location: LocationGambitDto) => {
  if (selectedLocationId.value === location.id) return;
};

const isAuthenticated = computed(() => authStore.isAuthenticated());

const isSidePanelCoveringMap = computed(() => {
  if (!isMobile.value) return false;
  const panel = route.query.side_panel;
  return !!panel && panel !== 'add_location';
});

const isSpotOpen = computed(() => !!route.query.spot);

const isListOpen = computed(() => route.query.side_panel === 'search' && !isMobile.value);

const showMapModes = computed(
  () =>
    !isSidePanelCoveringMap.value &&
    !isSpotOpen.value &&
    !mapStore.seedPickerMode &&
    !isCreateFlow.value &&
    !(isAddingLocation.value && isMobile.value),
);

const isCreateFlow = computed(
  () => route.query.side_panel === 'add_location' && route.query.mobile_step === 'pick_coordinate',
);

const isAddingLocation = computed(() => route.query.side_panel === 'add_location');

const showMapNav = computed(
  () =>
    !isSidePanelCoveringMap.value &&
    !isCreateFlow.value &&
    !isSpotOpen.value &&
    !isAddingLocation.value &&
    !proposalMode.value,
);

/**
 * A drawer takes the bottom of the screen over from ScreenNav, so the controls
 * only need to clear the nav's labels when no drawer is open — that keeps them
 * level with the pill rather than with the text below it.
 */
const controlsBottomGap = computed(() =>
  mapStore.bottomDrawerHeight > 0 ? '0.75rem' : 'calc(var(--safe-bottom-offset) + var(--screen-nav-label-offset))',
);

const goToSparks = () => {
  absorbGhostClick();
  router.push({ name: RouterNameType.SPARKS, query: { from: 'map' } });
};

const startMapProposal = () => {
  mapStore.enterProposalMode();
};

const { absorb: absorbGhostClick } = useGhostClickGuard();

const goToProfile = () => {
  absorbGhostClick();
  if (userDetails.value?.username) {
    router.push({
      name: RouterNameType.USERNAME,
      params: { username: userDetails.value.username },
      query: { from: 'map' },
    });
  }
};

// A stationary device can go silent between the tracker's forced refreshes
// (up to ~75s), so only call a fix stale well past that.
const GPS_STALE_THRESHOLD_MS = 90_000;
const now = useNow({ interval: 10_000 });

const isLocationStale = computed(() => {
  if (!isLocationPermissionGranted.value) return false;
  const lastUpdate = userLocation.value?.updatedAt;
  if (!lastUpdate) return true;
  return now.value.getTime() - new Date(lastUpdate).getTime() > GPS_STALE_THRESHOLD_MS;
});

const gpsButtonClasses = computed(() => [
  'map-nav-gps',
  {
    'gps-granted': isLocationPermissionGranted.value && !isLocationStale.value,
    'gps-stale': isLocationPermissionGranted.value && isLocationStale.value,
    'gps-prompt': !isLocationPermissionGranted.value && !isLocationPermissionDenied.value,
    'gps-denied': isLocationPermissionDenied.value,
  },
]);

const getSatelliteButton = computed((): GamButtonType => {
  return {
    leftIcon: satelliteMode.value ? GamIconName.MAP_2D : GamIconName.MAP_3D,
    variant: GamButtonVariant.SECONDARY,
    size: GamButtonSize.SMALL,
    isIconOnly: true,
    onClick: () => {
      mapStore.toggleSatelliteMode();
    },
  };
});

const resetMapHeading = () => {
  // @ts-ignore
  const map = window.gMap?.map;
  if (map) {
    map.setHeading(0);
    map.setTilt(0);
  }
};

const getCompassButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.COMPASS,
    variant: GamButtonVariant.SECONDARY,
    size: GamButtonSize.SMALL,
    isIconOnly: true,
    onClick: resetMapHeading,
  };
});

let isGpsCtaInFlight = false;

const handlePreciseLocationRequest = async () => {
  if (isGpsCtaInFlight) return;

  if (!isLocationPermissionGranted.value) {
    isGpsCtaInFlight = true;
    try {
      const coords = await geolocationStore.requestFreshPosition();
      if (!coords) {
        if (isLocationPermissionDenied.value) {
          notifyLocationDenied();
        } else {
          notifyLocationUnavailable();
        }
        return;
      }
      gambits.geolocationService.startTracking();

      const loc = { lat: coords.latitude, lng: coords.longitude };
      // @ts-ignore
      window.gMap?.map.setZoom(17);
      // @ts-ignore
      window.gMap?.map?.panTo(loc);
    } finally {
      isGpsCtaInFlight = false;
    }
    return;
  }

  // Immediate feedback: snap to last known GPS position
  const lastKnown = userLocation.value?.location;
  if (lastKnown) {
    // @ts-ignore
    window.gMap?.map.setZoom(17);
    // @ts-ignore
    window.gMap?.map?.panTo(lastKnown);
  }

  gambits.geolocationService.stopTracking();
  gambits.geolocationService.startTracking();

  isGpsCtaInFlight = true;
  try {
    const coords = await geolocationStore.requestFreshPosition();
    const loc = coords ? { lat: coords.latitude, lng: coords.longitude } : null;

    if (loc) {
      // @ts-ignore
      window.gMap?.map.setZoom(17);
      // @ts-ignore
      window.gMap?.map?.panTo(loc);
    }
  } finally {
    isGpsCtaInFlight = false;
  }
};

const getMapConfig = computed((): GamMapType => {
  return {
    selectedLocation: selectedItem.value,
  };
});

watch(openResultListSignal, () => {
  router.push({
    ...route,
    query: {
      ...route.query,
      side_panel: route.query.side_panel === 'search' ? undefined : 'search',
    },
  });
});

watch(addSpotSignal, () => {
  mapStore.enterProposalMode();
});

const handleExitProposalMode = () => {
  const walkRole = mapStore.walkSpotRole;
  const wasFromReveal = mapStore.walkFromReveal;
  if (walkRole) {
    walkStore.setSpot(walkRole, null);
  }
  proposalConfirmed.value = false;
  mapStore.exitProposalMode();
  if (walkRole) {
    router.push({
      name: RouterNameType.WALK,
      query: wasFromReveal ? { reveal: 'true' } : undefined,
    });
  }
};

const handleClearProposalCandidate = () => {
  const prev = mapProposalLocation.value;
  if (prev && prev.id !== mapStore.VIRTUAL_LOCATION_ID && !mapStore.walkSpotRole) {
    gambits.locationService.updateMarker(prev, undefined);
  }
  mapStore.setMapProposalLocation(null);
};

const isConfirmingWalkSpot = ref(false);

const handleConfirmProposalCandidate = async () => {
  const walkRole = mapStore.walkSpotRole;
  if (walkRole && mapProposalLocation.value) {
    const spot = { ...mapProposalLocation.value };

    if (spot.id === mapStore.VIRTUAL_LOCATION_ID) {
      if (isConfirmingWalkSpot.value) return;
      isConfirmingWalkSpot.value = true;
      try {
        const result = await handleCreateLocation({
          name: spot.name || spot.address || 'New spot',
          address: spot.address || '',
          type: LocationType.CHESS_FRIENDLY,
          coordinates: spot.coordinates,
          chessFeatures: [],
          generalFeatures: [],
          photos: [],
          private: false,
        });
        if (!result || typeof result !== 'object' || !('id' in result)) return;
        spot.id = (result as MapLocationDto).id;
      } finally {
        isConfirmingWalkSpot.value = false;
      }
    }

    const wasFromReveal = mapStore.walkFromReveal;
    if (wasFromReveal) {
      walkStore.saveSessionSpot(walkRole, spot);
    } else {
      walkStore.setSpot(walkRole, spot);
    }
    mapStore.exitProposalMode();
    router.push({
      name: RouterNameType.WALK,
      query: wasFromReveal ? { reveal: 'true' } : undefined,
    });
    return;
  }
  proposalConfirmed.value = true;
};

const isProposalCandidateVirtual = computed(() => mapProposalLocation.value?.id === mapStore.VIRTUAL_LOCATION_ID);

// New spots need the name/address step; existing spots go straight to the proposal card.
// Walk spot picking always confirms here, as that drawer is its only selection step.
const needsSpotConfirmation = computed(() => isProposalCandidateVirtual.value || !!mapStore.walkSpotRole);

const showProposalDrawer = computed(
  () =>
    proposalMode.value &&
    !mapStore.editGambitId &&
    (!mapProposalLocation.value || (needsSpotConfirmation.value && !proposalConfirmed.value)),
);

const showProposalCard = computed(
  () =>
    isAuthenticated.value &&
    !!mapProposalLocation.value &&
    (!proposalMode.value || !!mapStore.editGambitId || proposalConfirmed.value || !needsSpotConfirmation.value),
);

// Picking another candidate re-runs the confirmation step for new spots.
watch(mapProposalLocation, () => {
  proposalConfirmed.value = false;
});

const onProposalSearchSelected = (loc: GamDropdownItem | null) => {
  if (!loc || !loc.coordinates) return;

  // @ts-ignore
  const gMap = window.gMap?.map;

  if (loc.source === 'internal') {
    mapStore.setMapProposalLocation({
      mapLocationType: MapLocationType.LOCATION,
      id: loc.id,
      coordinates: loc.coordinates,
      name: loc.label,
      address: loc.subLabel,
    });
  } else {
    mapStore.setMapProposalLocation({
      mapLocationType: MapLocationType.LOCATION,
      id: mapStore.VIRTUAL_LOCATION_ID,
      coordinates: loc.coordinates,
      name: loc.label || '',
      address: loc.subLabel || `${loc.coordinates.lat.toFixed(5)}, ${loc.coordinates.lng.toFixed(5)}`,
    });
  }

  if (gMap) {
    gMap.setZoom(17);
    gMap.panTo(loc.coordinates);
    gMap.panBy(0, 150);
  }
};

const walkSpotName = computed(() => {
  const role = mapStore.walkSpotRole;
  if (!role) return undefined;
  const spot = role === 'start' ? walkStore.startSpot : walkStore.revealSpot;
  return spot?.name || undefined;
});

const getProposalSearchConfig = computed(
  (): GamSimpleInputSearchType<GamDropdownItem> => ({
    id: GamListId.LOCATION_SEARCH,
    isSearch: true,
    input: {
      name: GamInputName.SEARCH,
      placeholder: 'locations.map.proposal.search.placeholder',
      leftIcon: GamIconName.SEARCH,
      canClear: true,
      value: walkSpotName.value,
    },
    dropdown: {
      id: GamListId.LOCATION_SEARCH,
      reversed: true,
    },
    selectCallback: onProposalSearchSelected,
    onClickClear: () => {
      const role = mapStore.walkSpotRole;
      if (role) walkStore.setSpot(role, null);
    },
    loadCallback: async (query: string) => {
      const result = await gambits.locationService.searchLocationsByQuery(query, {
        mapbox: true,
        internal: true,
      });
      return {
        ...result,
        data: LocationHelper.mapSearchLocations(result.data),
      };
    },
  }),
);

const handleCheckInClose = () => {
  const savedLocation = mapProposalLocation.value;
  const isVirtual = savedLocation?.id === mapStore.VIRTUAL_LOCATION_ID;
  const spotStillOpen = !!route.query.spot;
  if (savedLocation && !isVirtual) {
    gambits.locationService.updateMarker(savedLocation, spotStillOpen ? savedLocation.id : undefined);
  }
  mapStore.exitCheckInMode();
};

const handleCheckInComplete = () => {
  mapStore.exitCheckInMode();
  const center = mapStore.currentLocation;
  if (center) {
    markerStore.addUniqueItems(center);
  }
};

const handleMapProposalClose = () => {
  const savedLocation = mapProposalLocation.value;
  const isVirtual = savedLocation?.id === mapStore.VIRTUAL_LOCATION_ID;
  const spotStillOpen = !!route.query.spot;
  proposalConfirmed.value = false;
  mapStore.setMapProposalLocation(null);
  if (proposalMode.value) {
    mapStore.exitProposalMode();
  }
  if (savedLocation && !isVirtual) {
    gambits.locationService.updateMarker(savedLocation, spotStillOpen ? savedLocation.id : undefined);
  }
};

const handleMapProposalCreated = async (locationId: string) => {
  const wasChallenge = !!challengeUserId.value;
  const spotStillOpen = !!route.query.spot;

  proposalConfirmed.value = false;
  mapStore.setMapProposalLocation(null);
  if (proposalMode.value) {
    mapStore.exitProposalMode();
  }

  const result = await gambits.locationService.getLocation(locationId);
  if (result?.data) {
    const loc = result.data;
    const updatedLoc: MapLocationDto = {
      mapLocationType: MapLocationType.LOCATION,
      id: loc.id,
      coordinates: loc.coordinates,
      type: loc.type,
      name: loc.name,
      address: loc.address,
      nextGambits: loc.nextGambits?.map((g) => ({
        id: g.id,
        name: g.title,
        coordinates: g.locationPoint,
        start: g.start,
        end: g.end,
        category: g.gambitCategory,
        proposal: g.proposal,
      })),
      visitorCount: loc.visitors?.length ?? 0,
    };
    gambits.locationService.updateMarker(updatedLoc, spotStillOpen ? loc.id : undefined);

    if (loc.nextGambits) {
      tableStore.updateItem({ id: locationId, nextGambits: loc.nextGambits } as BaseLocationDto);
    }

    if (spotStillOpen) {
      router.replace({ ...route, query: { ...route.query, spot: undefined } });
    }

    if (!wasChallenge) {
      await nextTick();
      router.replace({ ...route, query: { ...route.query, spot: locationId } });
    }
  }
};

onBeforeMount(() => {
  if (!route.query.zoom) {
    router.replace({ ...route, query: { ...route.query, zoom: currentZoom.value } });
  }
});

let lastRefreshDate = '';

function refreshOnResume() {
  if (document.hidden || !authStore.isAuthenticated()) return;
  const today = new Date().toLocaleDateString('en-CA');
  if (today === lastRefreshDate) return;
  lastRefreshDate = today;
}

onMounted(async () => {
  initInstallPrompt();
  authStore.fetchUserInfo(true);
  if (authStore.isAuthenticated()) {
    seedStore.fetchStats();
    lastRefreshDate = new Date().toLocaleDateString('en-CA');
  }
  document.addEventListener('visibilitychange', refreshOnResume);

  if (route.query.plant === 'true') {
    const cleanQuery = Object.fromEntries(Object.entries(route.query).filter(([k]) => k !== 'plant'));
    router.replace({ ...route, query: cleanQuery });
    await nextTick();
    enterSpotPicker();
  }

  if (route.query.sphere === 'open') {
    const viewParam = route.query.view as string | undefined;
    if (viewParam === 'walks' || viewParam === 'traces') {
      router.replace({ name: RouterNameType.WALKS });
    } else {
      showBoardsOverlay.value = true;
      router.replace({ ...route, query: { ...route.query, sphere: undefined, view: undefined } });
    }
  }
});

onBeforeRouteLeave(() => {
  mapStore.setMapProposalLocation(null);
  mapStore.exitProposalMode();
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', refreshOnResume);
  appStore.setDrawerOpen(false);
  mapStore.bottomDrawerHeight = 0;
  mapStore.setMapProposalLocation(null);
  mapStore.exitProposalMode();
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.locations-view {
  --panel-offset: 0px;
  overflow: hidden;

  &.list-open {
    --panel-offset: 417px;

    @media (max-width: 1111px) {
      --panel-offset: 0px;
    }
  }

  &.add-open {
    --panel-offset: 417px;

    @media (max-width: 1111px) {
      --panel-offset: 0px;
    }
  }

  .map-wrapper {
    z-index: 1;
  }

  .proposal-mode-header {
    position: absolute;
    z-index: 1001;
    bottom: 0;
    left: var(--panel-offset);
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4rem var(--spacing-l) var(--safe-bottom-offset) var(--spacing-xl);
    background: linear-gradient(
      180deg,
      rgba(#181716, 0) 0%,
      rgba(#181716, 0.15) 9.5%,
      rgba(#181716, 0.8) 28%,
      #181716 58%
    );
    pointer-events: auto;
    color: var(--color-white-70);
    font-size: 16px;
  }

  .proposal-mode-close {
    flex-shrink: 0;
  }

  .checkin-message {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .checkin-message-sub {
    font-size: 15px;
    opacity: 0.7;
  }

  .map-ui {
    position: absolute;
    top: calc(1.75rem + var(--sai-top));
    left: var(--panel-offset);
    right: 0;
    z-index: 500;
    display: flex;
    justify-content: center;
    pointer-events: none;

    .aux-nav {
      pointer-events: auto;
    }
  }

  .walk-cta-group {
    position: absolute;
    z-index: 499;
    bottom: var(--safe-bottom-offset);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    cursor: pointer;
    overflow: visible;

    &.is-breathing .walk-cta-character {
      animation: marker-breathe 4s ease-in-out infinite;
    }

    @include press($transform: translateX(-50%));
  }

  @keyframes marker-breathe {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.12);
    }
  }

  .walk-cta-character {
    position: relative;
    width: 3.5rem;
    height: 3.5rem;
    touch-action: manipulation;
    transition: transform 200ms ease;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;

    @include hover {
      transform: scale(1.03);
    }
  }

  .walk-cta-simple {
    position: relative;
    z-index: 1;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 3.5rem;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
  }

  .cta-timer-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .walk-cta-simple-icon {
    width: 16px;
    height: 16px;
    filter: invert(1);
  }

  .walk-cta-count {
    font-size: 13px;
    font-weight: 700;
    color: #000;
    letter-spacing: 0.02em;
  }

  .walk-cta-rings {
    position: absolute;
    width: 10rem;
    height: 10rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  .walk-cta-text {
    color: var(--color-white-50);
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
  }

  .walk-cta-red-dot {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-red);
  }

  .walk-progress-emoji {
    display: inline-flex;
    align-items: center;
    margin-right: 2px;
  }

  .proposal-drawer-overlay {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 802;
    pointer-events: none;
  }

  .proposal-drawer-card {
    pointer-events: auto;
    border-radius: 1.5rem 1.5rem 0 0;
    border: 1px solid var(--color-white-10);
    border-bottom: 0;
    background: var(--color-dark-700);
    display: flex;
    flex-direction: column;
    z-index: 803;
  }

  .proposal-drawer-title {
    padding: 1rem 1.5rem 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-white);
    text-align: center;
  }

  .proposal-drawer-body {
    padding: 1.5rem 1.5rem 1rem;

    &.proposal-candidate {
      padding: 1.5rem;
    }
  }

  .proposal-hint-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-white-50);
    text-align: center;
    display: block;
  }

  .proposal-candidate {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-l);
  }

  .proposal-candidate-info {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    color: var(--color-white-70);
    min-width: 0;
  }

  .proposal-candidate-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-white);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .proposal-candidate-address {
    font-size: 13px;
    color: var(--color-white-50);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .proposal-drawer-ctas {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 0.75rem 1.5rem var(--safe-bottom-offset);
  }

  .proposal-cta-circle {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 3.5rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    color: var(--color-white-70);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 200ms linear;

    @include hover {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }

    @include press;
  }

  .drawer-slide-enter-active,
  .drawer-slide-leave-active {
    transition: opacity 200ms ease;
  }

  .drawer-slide-enter-from,
  .drawer-slide-leave-to {
    opacity: 0;
  }

  .mobile-map-modes {
    position: absolute;
    z-index: 510;
    right: 0.75rem;
    top: calc(1.5rem + var(--sai-top));
    opacity: 1;
    transition: opacity var(--transition);

    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: var(--spacing-md);

    pointer-events: none;

    :deep(.gam-button) {
      flex-shrink: 0;
      pointer-events: auto;
    }
  }

  .map-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex: 1 0 0;
    align-self: stretch;
    justify-content: center;
    background: var(--color-linear-gradient-vertical) var(--color-dark-700);
    overflow: hidden;
    padding-left: var(--panel-offset);
    transition: padding-left 300ms ease;

    &.is-absolute {
      position: absolute;
      background: none;
      top: 0;
      left: 0;
      padding-left: 0;
    }
  }

  .map-proposal-overlay {
    position: absolute;
    z-index: 700;
    bottom: var(--safe-bottom-offset);
    left: var(--spacing-l);
    right: var(--spacing-l);
    margin: 0 auto;
    max-width: 420px;
    pointer-events: auto;

    &.checkin-overlay {
      z-index: 1002;
      bottom: calc(1.5rem + 3.5rem + var(--spacing-xl));
    }
  }

  .map-legend-cta {
    position: absolute;
    z-index: 500;
    top: 50%;
    transform: translateY(-50%);
    left: calc(var(--panel-offset) + 0.75rem);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    color: var(--color-white-30);
    cursor: pointer;
    pointer-events: auto;
    touch-action: manipulation;
    transition: color 200ms ease;

    @include hover {
      color: var(--color-white-70);
    }

    @include press($transform: translateY(-50%));
  }

  .walk-action-ctas {
    position: absolute;
    z-index: 500;
    top: calc(1.5rem + 3rem + 0.75rem + var(--sai-top));
    left: calc(var(--panel-offset) + 0.75rem + 12px);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    pointer-events: none;
  }

  .map-nav-emoji-cta {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 2.75rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    touch-action: manipulation;
    transition: all 200ms linear;
    font-size: 16px;
    line-height: 1;

    @include hover {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
    }

    &.is-loading {
      pointer-events: none;
      animation: pulse-upload 1.2s ease-in-out infinite;
    }

    @include press;
  }

  .reveal-cta-square {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    background-color: #ffffff;
    display: block;
  }

  @keyframes pulse-upload {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }

  .map-nav-proposal {
    width: 3rem;
    height: 3rem;
    border-radius: 3rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    color: var(--color-white-70);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    touch-action: manipulation;
    transition: all 200ms linear;

    @include hover {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }
  }

  .map-nav-seed-wrapper {
    position: absolute;
    z-index: 500;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    pointer-events: auto;
    touch-action: manipulation;
    cursor: pointer;
  }

  .map-install-wrapper {
    position: absolute;
    z-index: 499;
    /* Stacks above the nav's duo pill, which occupies this corner. The extra
       row is the pill's icon band plus the gap .map-controls-right uses. */
    bottom: calc(var(--controls-bottom-gap) + 3rem + 0.5rem + var(--drawer-bottom-offset, 0px));
    left: 0.75rem;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    transition: bottom 250ms ease;
  }

  .map-controls-right {
    position: absolute;
    z-index: 500;
    bottom: calc(var(--controls-bottom-gap) + var(--drawer-bottom-offset, 0px));
    right: 0.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    pointer-events: none;
    transition: bottom 250ms ease;

    > * {
      pointer-events: auto;
    }

    :deep(.gam-button-container.is-secondary) {
      border: none;
    }

    .compass-btn {
      :deep(svg) {
        transform: scale(1.6) rotate(var(--compass-rotation, 0deg));
        transition: transform 150ms ease-out;
      }

      &.rotated :deep(svg) {
        color: var(--color-pink);
      }
    }
  }

  .map-nav-install {
    position: relative;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 12px;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 200ms linear;
    overflow: hidden;

    .install-icon-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .ios-install-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1;
  }

  .ios-install-popup {
    position: absolute;
    bottom: calc(100% + var(--spacing-md));
    left: 0;
    width: 220px;
    padding: var(--spacing-l);
    background: var(--color-dark-600);
    border: 1px solid var(--color-white-10);
    border-radius: var(--radius-l);
    color: var(--color-white-70);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-md);
    backdrop-filter: blur(12px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    z-index: 2;

    .ios-install-popup-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .ios-install-title {
      color: var(--color-white);
      font-weight: 600;
    }

    .ios-share-icon,
    .ios-add-icon {
      display: inline-block;
      width: 20px;
      height: 20px;
      vertical-align: middle;
      margin: 0 2px;
      color: var(--color-primary);
    }

    cursor: pointer;
  }

  .map-nav-gps {
    width: 3rem;
    height: 3rem;
    border-radius: 3rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    color: var(--color-white-70);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    touch-action: manipulation;
    transition: all 200ms linear;

    @include hover {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }

    &.gps-prompt,
    &.gps-stale {
      color: var(--color-pink);
    }

    &.gps-denied {
      color: var(--color-red);
      cursor: pointer;
    }
  }

  .seed-picker-top {
    position: absolute;
    z-index: 700;
    top: var(--spacing-l);
    left: var(--panel-offset);
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-md) var(--spacing-l);
    pointer-events: auto;

    .seed-picker-instruction {
      padding: 8px 16px;
      font-size: 13px;
      letter-spacing: 0.03em;
      color: var(--color-white-70);
      text-align: center;
      background: var(--color-dark-600);
      border: 1px solid var(--color-white-10);
      border-radius: var(--radius-small);
    }
  }

  .seed-picker-close {
    position: absolute;
    bottom: calc(1.5rem + 2.125rem);
    left: 50%;
    transform: translateX(-50%);
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 3.5rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    color: var(--color-white-70);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 700;
    pointer-events: auto;

    @include press($transform: translateX(-50%));
  }

  .gps-popup-enter-active,
  .gps-popup-leave-active {
    transition:
      opacity 150ms ease,
      transform 150ms ease;
  }

  .gps-popup-enter-from,
  .gps-popup-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.preview-slide-enter-active,
.preview-slide-leave-active {
  transition:
    transform 250ms ease,
    opacity 250ms ease;
}

.preview-slide-enter-from,
.preview-slide-leave-to {
  transform: translateY(1rem);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
